import appSettings from '@/appsettings.json';
import { tourSteps } from "@/context/tour/tutorial-tour";
import ConductionAPI, { ArtistStats, CurrentTos, PagedResults } from "@/javascript/conduction-api";
import { Song } from "@/javascript/library-objects";
import { Instrument } from "@/javascript/music-objects";
import { ProjectSubmission } from "@/javascript/project-objects";
import { theme } from "@/theme";
import { Avatar } from '@mantine/core';
import { IconAlertCircle, IconAt, IconCamera, IconUserCheck } from "@tabler/icons-react";
import React, { createContext, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import Confetti from 'react-confetti';
import Joyride, { CallBackProps, STATUS, StoreHelpers } from "react-joyride";
import { useNavigate } from "react-router-dom";
import { Classroom, User } from "../javascript/user-objects";
import { Utils } from "../javascript/utils";

export interface SessionContextType {
	loadingUserInfo: boolean;
	user: User | null;
	token: string | null;
	userSongs: Song[] | null;
	clearProjectSubmissionsCache: () => void;
	getProjectSubmissionsPage: (
		pageNumber: number,
		approvedOnly: boolean,
		unapprovedOnly: boolean,
		fulfilledOnly: boolean,
		associatedUserId: string | undefined,
		fulfilledTasksOnly: boolean,
		unfulfilledTasksOnly: boolean,
		classroomId: string | undefined,
		loadOffers?: boolean
	) => Promise<PagedResults<ProjectSubmission>>;
	getInstrumentalistProjectSubmissions: (userId: string) => Promise<PagedResults<ProjectSubmission>>;
	getVideoEditorProjectSubmissions: (userId: string) => Promise<PagedResults<ProjectSubmission>>;
	allSongs: Song[] | null;
	instruments: Instrument[] | null;
	administratingClassroom: Classroom | null | undefined;
	fetchAdministratingClassroom: (classroomId: string) => Promise<void>;
	artistStats: ArtistStats | null;
	setArtistStats: (artistStats: ArtistStats) => void;
	login: (usernameOrEmail: string, password: string, rememberMe: boolean, autoAgreeTOS?: boolean) => Promise<boolean>;
	logout: () => void;
	profileAvatar: JSX.Element;
	uploadProfileImage: (file: File) => Promise<void>;
	fetchProfileAvatar: (user: User) => Promise<JSX.Element>;
	changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
	changeInstruments: (newInstruments: string[]) => Promise<void>
	changeUsername: (newUsername: string) => Promise<void>;
	changeEmail: (newEmail: string) => Promise<void>;
	sendEmailConfirmation: () => Promise<void>;
	confirmEmail: (userId: string, emailToken: string) => Promise<void>;
	startTour: () => void
	tutorialTourActive: boolean,
	currentTourStep: number,
	moveToNextTourStep: () => void,
	moveToPrevTourStep: () => void,
	jumpToTourStep: (index: number) => void,
	fetchArtistStats: (artistId: string) => void,
	currentTos: CurrentTos | undefined;
	fetchCurrentTos: () => Promise<void>;
	agreeToTos: (version: string) => Promise<boolean>;
}

export const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const AVATAR_SIZE = 34;
export const PROJECT_SUBMISSIONS_PER_PAGE = 8; // Number of project submissions to display per page; hardcoded
type ProjectSubmissionPageKey = string;
const makeProjectSubmissionsPageKey = (
	userId: string,
	size: number,
	page: number,
	approvedOnly: boolean,
	unapprovedOnly: boolean,
	fulfilledOnly: boolean,
	associatedUserId: string | undefined,
	fulfilledTasksOnly: boolean,
	unfulfilledTasksOnly: boolean,
	classroomId: string | undefined,
	loadOffers: boolean = false
): ProjectSubmissionPageKey =>
	[
		userId,
		size,
		page,
		approvedOnly,
		unapprovedOnly,
		fulfilledOnly,
		associatedUserId ?? "",
		fulfilledTasksOnly,
		unfulfilledTasksOnly,
		classroomId ?? "",
		loadOffers
	].join(":");

export const SessionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	// Session values
	const [loadingUserInfo, setLoadingUserInfo] = useState(false);
	const [user, setUser] = useState<User | null>(null);
	const [userSongs, setUserSongs] = useState<Song[] | null>(null);
	const [allSongs, setAllSongs] = useState<Song[] | null>(null);
	const [instruments, setInstruments] = useState<Instrument[] | null>([]);
	const [artistStats, setArtistStats] = useState<ArtistStats | null>(null)
	const [profileAvatar, setProfileAvatar] = useState<JSX.Element>(<Avatar radius="xl" size={AVATAR_SIZE} />);

	// Caching for project submission pages
	const projectSubmissionCacheRef = useRef<Map<ProjectSubmissionPageKey, PagedResults<ProjectSubmission>>>(new Map());
	const inflightRef = useRef<Map<ProjectSubmissionPageKey, Promise<PagedResults<ProjectSubmission>>>>(new Map());
	const requestSeqRef = useRef<number>(0);

	// Stored session values
	const getStoredUserId = () => sessionStorage.getItem("userId") || localStorage.getItem("userId");
	const getStoredToken = () => sessionStorage.getItem("authToken") || localStorage.getItem("authToken");
	const [userId, setUserId] = useState<string | null>(getStoredUserId());
	const [token, setToken] = useState<string | null>(getStoredToken());

	// Tutorial tour management
	const [currentTourStep, setCurrentTourStep] = useState<number>(0);
	const [tutorialTourActive, setTutorialTourActive] = useState(false);
	const tourHelpersRef = useRef<StoreHelpers | null>(null);

	const [currentTos, setCurrentTos] = useState<CurrentTos | undefined>(undefined);

	const navigate = useNavigate();

	// Fetch the current user based on stored token
	const fetchUser = async () => {
		if (!userId || !token) {
			return;
		}

		setLoadingUserInfo(true);
		try {
			const user = await ConductionAPI.getUser(userId, token)
			if (user != undefined) {
				setUser(user);
			} else {
				logout(); // Clear token if invalid
			}
		} catch (error) {
			console.error("Error fetching user:", error);
			logout(); // Clear token if invalid
		} finally {
			setLoadingUserInfo(false);
		}
	};

	const fetchUserSongs = useCallback(async () => {
		if (user === null || token === null || user.roles.includes("Guest")) {
			// User songs can only be fetched after login and not for guests
			return;
		}

		try {
			const loadedSongs = await ConductionAPI.getUserSongs(token);

			if (!loadedSongs) {
				return;
			}
			setUserSongs(loadedSongs.sort((a, b) => b.uploadDate.getTime() - a.uploadDate.getTime())); // Newest first

		} catch (error) {
			console.error('Error fetching songs:', error);
			Utils.displayNotification(
				'Library Load Failure',
				'Something went wrong when loading the library. Please try again later.',
				"red",
				<IconAlertCircle size={16} />,
				true,
				false
			);
		}
	}, [user, token]);

	const getProjectSubmissionsPage = useCallback(async (
		pageNumber: number,
		approvedOnly: boolean,
		unapprovedOnly: boolean,
		fulfilledOnly: boolean,
		associatedUserId: string | undefined,
		fulfilledTasksOnly: boolean,
		unfulfilledTasksOnly: boolean,
		classroomId: string | undefined,
		loadOffers: boolean = false
	): Promise<PagedResults<ProjectSubmission>> => {
		if (user === null || token === null) {
			throw new Error("User not authorized");
		}
		if (fulfilledTasksOnly && unfulfilledTasksOnly) {
			throw new Error("Choose either fulfilledTasksOnly or unfulfilledTasksOnly, not both.");
		}

		const page = Math.max(1, pageNumber);
		const key = makeProjectSubmissionsPageKey(
			user.id,
			PROJECT_SUBMISSIONS_PER_PAGE,
			page,
			approvedOnly,
			unapprovedOnly,
			fulfilledOnly,
			associatedUserId,
			fulfilledTasksOnly,
			unfulfilledTasksOnly,
			classroomId,
			loadOffers
		);

		// 1) Cache hit
		const cached = projectSubmissionCacheRef.current.get(key);
		if (cached) return cached;

		// 2) Deduplicate in-flight requests for the same key
		let promise = inflightRef.current.get(key);
		if (!promise) {
			const seq = ++requestSeqRef.current;

			// NOTE: (page, pageSize) order
			promise = ConductionAPI.getProjectSubmissionsPage(
				token,
				page,
				PROJECT_SUBMISSIONS_PER_PAGE,
				approvedOnly,
				unapprovedOnly,
				fulfilledOnly,
				associatedUserId,
				fulfilledTasksOnly,
				unfulfilledTasksOnly,
				classroomId,
				loadOffers
			)
				.then((res) => {
					if (!res) throw new Error("Failed to load project submissions page.");
					// Only cache if this call is still the latest
					if (seq === requestSeqRef.current) {
						projectSubmissionCacheRef.current.set(key, res);
					}
					return res;
				})
				.finally(() => {
					inflightRef.current.delete(key);
				});

			inflightRef.current.set(key, promise);
		}

		const loaded = await promise; // guaranteed by the throw above

		// 3) Prefetch the next page (best-effort)
		if (loaded.page < loaded.totalPages) {
			const nextKey = makeProjectSubmissionsPageKey(
				user.id,
				PROJECT_SUBMISSIONS_PER_PAGE,
				loaded.page + 1,
				approvedOnly,
				unapprovedOnly,
				fulfilledOnly,
				associatedUserId,
				fulfilledTasksOnly,
				unfulfilledTasksOnly,
				classroomId,
				loadOffers
			);

			if (!projectSubmissionCacheRef.current.has(nextKey) && !inflightRef.current.has(nextKey)) {
				const prefetch = ConductionAPI.getProjectSubmissionsPage(
					token,
					loaded.page + 1,
					PROJECT_SUBMISSIONS_PER_PAGE,
					approvedOnly,
					unapprovedOnly,
					fulfilledOnly,
					associatedUserId,
					fulfilledTasksOnly,
					unfulfilledTasksOnly,
					classroomId
				).then((res) => {
					if (res) projectSubmissionCacheRef.current.set(nextKey, res);
					return res;
				}).finally(() => inflightRef.current.delete(nextKey));

				inflightRef.current.set(nextKey, prefetch);
			}
		}

		return loaded;
	}, [user, token]);

	const getInstrumentalistProjectSubmissions = useCallback(async (userId: string): Promise<PagedResults<ProjectSubmission>> => {
		if (user === null || token === null) {
			throw new Error("User not authorized");
		}
		const key = `instrumentalist:${userId}`;

		// 1) Cache hit
		const cached = projectSubmissionCacheRef.current.get(key);
		if (cached) return cached;

		// 2) Deduplicate in-flight requests for the same key
		let promise = inflightRef.current.get(key);
		if (!promise) {
			const seq = ++requestSeqRef.current;

			// NOTE: (page, pageSize) order
			promise = ConductionAPI.getInstrumentalistProjectSubmissions(
				token,
				userId
			)
				.then((res) => {
					if (!res) throw new Error("Failed to load project submissions page.");
					// Only cache if this call is still the latest
					if (seq === requestSeqRef.current) {
						projectSubmissionCacheRef.current.set(key, res);
					}
					return res;
				})
				.finally(() => {
					inflightRef.current.delete(key);
				});

			inflightRef.current.set(key, promise);
		}

		return await promise; // guaranteed by the throw above
	}, [user, token]);

	const getVideoEditorProjectSubmissions = useCallback(async (userId: string): Promise<PagedResults<ProjectSubmission>> => {
		if (user === null || token === null) {
			throw new Error("User not authorized");
		}
		const key = `video-editor:${userId}`;

		// 1) Cache hit
		const cached = projectSubmissionCacheRef.current.get(key);
		if (cached) return cached;

		// 2) Deduplicate in-flight requests for the same key
		let promise = inflightRef.current.get(key);
		if (!promise) {
			const seq = ++requestSeqRef.current;

			// NOTE: (page, pageSize) order
			promise = ConductionAPI.getVideoEditorProjectSubmissions(
				token,
				userId
			)
				.then((res) => {
					if (!res) throw new Error("Failed to load project submissions page.");
					// Only cache if this call is still the latest
					if (seq === requestSeqRef.current) {
						projectSubmissionCacheRef.current.set(key, res);
					}
					return res;
				})
				.finally(() => {
					inflightRef.current.delete(key);
				});

			inflightRef.current.set(key, promise);
		}

		return await promise; // guaranteed by the throw above
	}, [user, token]);

	const clearProjectSubmissionsCache = useCallback(() => {
		// Clear caches (and cancel semantics via seq bump)
		projectSubmissionCacheRef.current.clear();
		inflightRef.current.clear();
		requestSeqRef.current++;
	}, [user, token, getProjectSubmissionsPage]);

	const fetchAllSongs = useCallback(async () => {
		try {
			const loadedSongs = await ConductionAPI.getMarketplaceSongs();

			if (!loadedSongs) {
				return;
			}
			setAllSongs(loadedSongs);
		} catch (error) {
			console.error('Error fetching songs:', error);
			Utils.displayNotification(
				'Library Load Failure',
				'Something went wrong when loading the library. Please try again later.',
				"red",
				<IconAlertCircle size={16} />,
				true,
				false
			);
		}
	}, []);

	const fetchInstruments = useCallback(async () => {
		try {
			const loadedInstruments = await ConductionAPI.getInstruments();

			if (!loadedInstruments) {
				return;
			}
			setInstruments(loadedInstruments);
		} catch (error) {
			console.error('Error fetching instruments:', error);
			Utils.displayNotification(
				'Instrument Load Failure',
				'Something went wrong when loading the instruments. Please try again later.',
				"red",
				<IconAlertCircle size={16} />,
				true,
				false
			);
		}
	}, []);

	const fetchMyProfileAvatar = useCallback(async () => {
		if (!user?.id || !token) {
			return;
		}

		setProfileAvatar(await fetchProfileAvatar(user));
	}, [user?.id, token]);

	const fetchProfileAvatar = useCallback(async (user: User) => {
		const getAvatarFallback = () => {
			const initials = (user.firstName?.[0] ?? user.username?.[0] ?? "").toUpperCase() + (user.lastName?.[0] ?? "").toUpperCase();
			const color = Utils.getColorFromString(user.id);
			return (
				<Avatar
					alt={user.username}
					radius="xl"
					size={AVATAR_SIZE}
					name={initials}
					color="initials"
					allowedInitialsColors={[color]}
					key={user.id}
				/>
			);
		};

		if (user.uploadedProfileImage) {
			try {
				const image = await fetchProfileAvatarImage(user.id);
				return <Avatar src={image} alt={user.username} radius="xl" size={AVATAR_SIZE} key={user.id} />;
			} catch (error) {
				console.error('Error fetching profile image:', error);
				return getAvatarFallback();
			}
		} else {
			return getAvatarFallback();
		}
	}, []);

	const fetchProfileAvatarImage = useCallback(async (userId: string) => {
		if (!userId) {
			return null;
		}
		try {
			const timestamp = new Date().getTime();
			const imageUrl = `${appSettings.S3BucketBaseUrl}ProfileImages/${userId}.jpg?cb=${timestamp}`;

			const res = await fetch(imageUrl, { method: "GET", cache: "no-cache" });
			if (res.ok) {
				return imageUrl;
			} else {
				return null; // Image not found
			}
		} catch (error) {
			return null;
		}
	}, []);

	const fetchArtistStats = useCallback(async (artistId: string) => {
		try {
			if (user == null) {
				return;
			}

			const loadedStats = await ConductionAPI.getArtistStats(artistId);

			if (!loadedStats) {
				return;
			}
			setArtistStats(loadedStats);
		} catch (error) {
			console.error('Error fetching stats:', error);
		}
	}, [user]);

	// Load instruments on initial render
	useEffect(() => {
		fetchInstruments();
	}, []);

	// Load songs when the user changes
	useEffect(() => {
		if (userId === null || token === null) return;

		fetchUserSongs();
		fetchAllSongs();
		fetchMyProfileAvatar();
	}, [user?.id, token]);

	// Load user when token changes
	useEffect(() => {
		if (userId === null) {
			setUser(null);
			setToken(null);
			return;
		}
		fetchUser();
	}, [userId, token]);

	// Login function for regular user
	const login = async (usernameOrEmail: string, password: string, rememberMe: boolean, autoAgreeTOS?: boolean): Promise<boolean> => {
		const notificaitonId = Utils.displayNotification("Attempting Login...", "This may take a few seconds.", "blue", "loading");
		try {
			const loginResults = await ConductionAPI.login(usernameOrEmail, password, rememberMe);
			if (loginResults === null) {
				const icon = <IconAlertCircle size={16} />;
				Utils.updateNotification(notificaitonId, "Login Failed", "The provided username or password was incorrect.", "red", icon);
				return false;
			}
			const loggedInUser = loginResults.user;
			const loggedInToken = loginResults.token;

			setUserId(loggedInUser.id)
			setToken(loggedInToken);

			// Store token based on rememberMe preference
			if (rememberMe) {
				localStorage.setItem("userId", loggedInUser.id)
				localStorage.setItem("authToken", loggedInToken);
			} else {
				sessionStorage.setItem("userId", loggedInUser.id);
				sessionStorage.setItem("authToken", loggedInToken);
			}

			if (autoAgreeTOS) {
				if (currentTos != null) {
					const locale = navigator.language || 'en-US';
					await ConductionAPI.acceptTos(currentTos.version, loggedInToken, locale);
				}
			}

			const icon = <IconUserCheck size={16} />;
			Utils.updateNotification(notificaitonId, "Welcome back to Conduction!", null, "green", icon);
			return true;
		} catch (error) {
			const icon = <IconAlertCircle size={16} />;
			Utils.updateNotification(notificaitonId, "App Failure", "We are sorry, but an error has occurred during the login process. Please try again later.", "red", icon, true, false);
			console.error("Login failed:", error);
			return false;
		}
	};

	const uploadProfileImage = useCallback(async (file: File) => {
		if (!user || !token) {
			throw new Error("User must be logged in to upload profile image.");
		}

		const notificationId = Utils.displayNotification("Uploading Profile Image", null, "blue", "loading");
		try {
			await ConductionAPI.uploadProfileImage(file, token);
			Utils.updateNotification(notificationId, "Profile Image Updated", null, "green", <IconCamera size={16} />);

			// Refresh the profile image
			fetchMyProfileAvatar();
		} catch (error) {
			Utils.updateNotification(notificationId, "Profile Image Update Failed", "There was an error updating your profile image. Please try again later.", "red", <IconAlertCircle size={16} />, true, false);
			console.error("Error uploading profile image:", error);
		}
	}, [user, token]);

	const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
		if (!user || !token) {
			throw new Error("User must be logged in to change password.");
		}
		if (!currentPassword || currentPassword.trim() === "" || !newPassword || newPassword.trim() === "") {
			Utils.displayNotification("Password Missing", "Please enter a valid current and new password.", "red", <IconAt size={16} />);
			return;
		}
		const notificationId = Utils.displayNotification("Changing Password", null, "blue", "loading");
		try {
			await ConductionAPI.changePassword(currentPassword, newPassword, token);
			Utils.updateNotification(notificationId, "Password Changed", null, "green", <IconAt size={16} />);
		} catch (error) {
			Utils.updateNotification(notificationId, "Password Change Failed", "You may have entered an incorrect current password.", "red", <IconAlertCircle size={16} />, true, false);
			console.error("Error changing password:", error);
		}
	}, [user, token]);

	const changeInstruments = useCallback(async (newInstruments: string[]) => {
		if (!user || !token) {
			throw new Error("User must be logged in to change instruments.");
		}

		const notificationId = Utils.displayNotification("Changing Instruments", null, "blue", "loading");
		try {
			await ConductionAPI.changeInstruments(newInstruments, token);
			// Create a list of instruments by copying the corresponding objects from the instruments list
			const foundInstruments = instruments?.filter(i => newInstruments.some(newInstrumentName => newInstrumentName === i.name)) ?? [];
			setUser(prev => prev ? { ...prev, instruments: foundInstruments } : null);
			Utils.updateNotification(notificationId, "Instruments Changed", null, "green", <IconAt size={16} />);
		} catch (error) {
			Utils.updateNotification(notificationId, "Instrument Changing Failed", "There was an error updating your instruments. Please try again later.", "red", <IconAlertCircle size={16} />, true, false);
			console.error("Error changing password:", error);
		}
	}, [user, token]);

	const changeUsername = useCallback(async (newUsername: string) => {
		if (!user || !token) {
			throw new Error("User must be logged in to change username.");
		}
		if (!newUsername || newUsername.trim() === "") {
			Utils.displayNotification("Username Missing", "Please enter a valid username.", "red", <IconAt size={16} />);
			return;
		}
		if (newUsername === user.username) {
			Utils.displayNotification("Username Unchanged", "The new username is the same as the current one.", "yellow", <IconAt size={16} />);
			return;
		}

		const notificationId = Utils.displayNotification("Changing Username", null, "blue", "loading");
		try {
			await ConductionAPI.changeUsername(newUsername, token);
			setUser(prev => prev ? { ...prev, username: newUsername } : null);
			Utils.updateNotification(notificationId, "Username Changed", null, "green", <IconAt size={16} />);
		} catch (error) {
			Utils.updateNotification(notificationId, "Username Change Failed", "This username may already be in use by a different account.", "red", <IconAlertCircle size={16} />, true, false);
			console.error("Error changing username:", error);
		}
	}, [user, token]);

	const changeEmail = useCallback(async (newEmail: string) => {
		if (!user || !token) {
			throw new Error("User must be logged in to change email.");
		}

		if (!newEmail || newEmail.trim() === "") {
			Utils.displayNotification("Email Missing", "Please enter a valid email address.", "red", <IconAt size={16} />);
			return;
		}

		if (newEmail === user.email) {
			Utils.displayNotification("Email Unchanged", "The new email address is the same as the current one.", "yellow", <IconAt size={16} />);
			return;
		}

		const notificationId = Utils.displayNotification("Changing Email", null, "blue", "loading");
		try {
			await ConductionAPI.changeEmail(newEmail, token);
			setUser(prev => prev ? { ...prev, email: newEmail, emailConfirmed: false } : null);

			Utils.updateNotification(notificationId, "Sending Email Confirmation", null, "blue", "loading");
			try {
				await ConductionAPI.sendEmailConfirmation(token);
				Utils.updateNotification(notificationId, "Email Confirmation Sent", `An email was sent to ${newEmail}. Please click the link in that email to complete the confirmation process.`, "blue", <IconAt size={16} />, true, false);
			} catch (error) {
				Utils.updateNotification(notificationId, "Email Confirmation Failed", "There was an error sending the confirmation email. Please try again later.", "red", <IconAlertCircle size={16} />, true, false);
				console.error("Error sending email confirmation:", error);
			}
		} catch (error) {
			Utils.updateNotification(notificationId, "Email Change Failed", "This email address may already be linked to a different account.", "red", <IconAlertCircle size={16} />, true, false);
			console.error("Error changing email:", error);
		}
	}, [user, token]);

	const sendEmailConfirmation = useCallback(async () => {
		if (!user || !token) {
			throw new Error("User must be logged in to send email confirmation.");
		}

		const notificationId = Utils.displayNotification("Sending Email Confirmation", null, "blue", "loading");
		try {
			await ConductionAPI.sendEmailConfirmation(token);
			Utils.updateNotification(notificationId, "Email Confirmation Sent", `An email was sent to ${user.email}. Please click the link in that email to complete the confirmation process.`, "blue", <IconAt size={16} />, true, false);
		} catch (error) {
			Utils.updateNotification(notificationId, "Email Confirmation Failed", "There was an error sending the confirmation email. Please try again later.", "red", <IconAlertCircle size={16} />, true, false);
			console.error("Error sending email confirmation:", error);
		}
	}, [user, token]);

	const confirmEmail = useCallback(async (givenUserId: string, emailToken: string) => {
		if (!givenUserId || !emailToken) {
			throw new Error("User ID and email token are required to confirm email.");
		}

		const notificationId = Utils.displayNotification("Confirming Email", null, "blue", "loading");
		try {
			await ConductionAPI.confirmEmail(givenUserId, emailToken);
			if (user && user.id === givenUserId) {
				setUser(prev => prev ? { ...prev, emailConfirmed: true } : null);
			}
			Utils.updateNotification(notificationId, "Email Confirmed", null, "green", <IconUserCheck size={16} />, true, 10000);
		} catch (error) {
			Utils.updateNotification(notificationId, "Email Confirmation Failed", "There was an error confirming your email address. Please try again later.", "red", <IconAlertCircle size={16} />, true, false);
			console.error("Error confirming email:", error);
		}
	}, [user]);

	const agreeToTos = useCallback(async (version: string) => {
		if (!token) {
			throw new Error("User must be logged in to agree to the Terms of Service.");
		}

		try {
			const locale = navigator.language || 'en-US';
			await ConductionAPI.acceptTos(version, token, locale);
			setUser(prev => prev ? { ...prev, agreedToCurrentTos: true } : null);

			return true;
		} catch (error) {
			Utils.displayNotification("TOS Acceptance Failed", "There was an error recording your acceptance of the Terms of Service. Please try again later.", "red", <IconAlertCircle size={16} />, true, false);
			console.error("Error accepting Terms of Service:", error);

			return false;
		}
	}, [token]);

	const fetchingTosRef = useRef<boolean>(false);
	const fetchCurrentTos = useCallback(async () => {
		if (fetchingTosRef.current || currentTos !== undefined) return;
		fetchingTosRef.current = true;

		try {
			const currentTos = await ConductionAPI.getCurrentTos();
			setCurrentTos(currentTos ?? undefined);
		} catch (error) {
			console.error("Error fetching current Terms of Service:", error);
		} finally {
			fetchingTosRef.current = false;
		}
	}, [userId, token]);

	// Logout function
	const logout = useCallback(async () => {
		setUser(null);
		setUserId(null);
		setToken(null);
		setUserSongs(null);

		localStorage.removeItem("userId");
		sessionStorage.removeItem("userId");
		localStorage.removeItem("authToken");
		sessionStorage.removeItem("authToken");

		clearProjectSubmissionsCache();

		// Navigate to the home page
		navigate("/");
	}, [navigate, clearProjectSubmissionsCache]);

	// ----------------------
	// Classroom state management
	// ----------------------

	const [administratingClassroom, setAdministratingClassroom] = useState<Classroom | null | undefined>(undefined);

	const fetchAdministratingClassroom = useCallback(async (classroomId: string) => {
		if (token === null) {
			throw new Error("User must be logged in to fetch classroom.");
		}

		try {
			const loadedClassroom = await ConductionAPI.getClassroom(classroomId, token);
			setAdministratingClassroom(loadedClassroom);
		} catch (error) {
			setAdministratingClassroom(null);
			console.error("Error fetching classroom:", error);
		}
	}, [token]);

	// ----------------------
	// Artist state management
	// ----------------------

	useEffect(() => {
		// Load from storage on mount
		const cachedStats = localStorage.getItem("artistStats");
		if (cachedStats) {
			setArtistStats(JSON.parse(cachedStats));
		}
	}, []);

	useEffect(() => {
		// Cache stats on change
		if (artistStats) {
			localStorage.setItem("artistStats", JSON.stringify(artistStats));
		}
	}, [artistStats]);

	// ----------------------
	// Tour management
	// ----------------------

	const startTour = () => {
		setTutorialTourActive(true);
		jumpToTourStep(0);
	};

	const handleGetHelpers = (helpers: StoreHelpers) => {
		tourHelpersRef.current = helpers;
	};

	const moveToNextTourStep = () => {
		if (tourHelpersRef.current) {
			tourHelpersRef.current.next();
		}
	}

	const moveToPrevTourStep = () => {
		if (tourHelpersRef.current) {
			tourHelpersRef.current.prev();
		}
	}

	const jumpToTourStep = (index: number) => {
		if (tourHelpersRef.current) {
			tourHelpersRef.current.go(index);
		}
	};

	// Callback to handle tour events (e.g., finish or skip)
	const handleJoyrideCallback = (data: CallBackProps) => {
		const { status, index } = data;
		setCurrentTourStep(index);
		const tutorialFinished = status === STATUS.FINISHED;
		const tutorialSkipped = status === STATUS.SKIPPED;
		if (tutorialFinished || tutorialSkipped) {
			if (tutorialFinished) {
				confettiTime();
			}
			if (user !== null && token !== null && !user.roles.includes("Guest")) {
				// If the tutorial was actually completed or if the tutorial was skipped and the user has a project saved, we cam set the completion status true
				if (tutorialFinished || (tutorialSkipped && user.projectIds.length > 0)) {
					ConductionAPI.setTutorialCompletionStatus(user.id, true, token)
				}
			}
		}
	};

	// ----------------------
	// Bells and whistles
	// ----------------------
	const [showConfetti, setShowConfetti] = useState(false);

	const confettiTime = () => {
		setShowConfetti(true);
	}

	return (
		<SessionContext.Provider
			value={{
				loadingUserInfo,
				user,
				token,
				userSongs,
				clearProjectSubmissionsCache,
				getProjectSubmissionsPage,
				getInstrumentalistProjectSubmissions,
				getVideoEditorProjectSubmissions,
				allSongs,
				instruments,
				administratingClassroom,
				fetchAdministratingClassroom,
				artistStats,
				setArtistStats,
				login,
				logout,
				profileAvatar,
				uploadProfileImage,
				fetchProfileAvatar,
				changePassword,
				changeInstruments,
				changeUsername,
				changeEmail,
				sendEmailConfirmation,
				confirmEmail,
				startTour,
				tutorialTourActive,
				currentTourStep,
				moveToNextTourStep,
				moveToPrevTourStep,
				jumpToTourStep,
				fetchArtistStats,
				currentTos,
				fetchCurrentTos,
				agreeToTos
			}}
		>
			{/* React Joyride component for our tour */}
			<Joyride
				steps={tourSteps}
				run={tutorialTourActive}
				disableCloseOnEsc
				disableOverlayClose
				disableScrolling
				disableScrollParentFix
				continuous
				callback={handleJoyrideCallback}
				getHelpers={handleGetHelpers}
				styles={{
					options: {
						primaryColor: (theme.colors?.red)?.[6] // Custom color for the next button
					},
				}}
			/>

			{showConfetti &&
				<Confetti
					numberOfPieces={200}
					recycle={false}
					onConfettiComplete={() => setShowConfetti(false)}
				/>
			}

			{children}
		</SessionContext.Provider>
	);
};